import {useCallback, useEffect, useRef, useState} from 'react';
import llmUsageService, {LLMUsageReport} from '../services/llmUsage.service';

const POLL_INTERVAL_MS = 60_000;

export interface UseLlmUsageReturn {
    /** The latest report, or null until the first one arrives. */
    report: LLMUsageReport | null;
    /** Why the latest request failed, if it did. */
    error: string | null;
    /** True until the first request settles. */
    loading: boolean;
    /** True while a refresh asked for by the reader is in flight. */
    refreshing: boolean;
    /** When the shown report arrived. */
    loadedAt: Date | null;
    /** Asks for the report again now. */
    refresh: () => Promise<void>;
}

/**
 * The LLM usage report, read when the page opens and every minute after, while enabled.
 *
 * <p>A refresh can overlap a poll, so each request takes a sequence number and only the newest one's
 * answer is kept: an older report arriving late never replaces a newer one. Nothing is set once the
 * page has gone.</p>
 *
 * @param enabled false for a reader who may not see the report, so nothing is asked for
 */
export function useLlmUsage(enabled: boolean): UseLlmUsageReturn {
    const [report, setReport] = useState<LLMUsageReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(enabled);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [loadedAt, setLoadedAt] = useState<Date | null>(null);
    const latestRequest = useRef(0);
    const mounted = useRef(true);

    const load = useCallback(async (): Promise<void> => {
        const request = ++latestRequest.current;
        try {
            const next = await llmUsageService.getUsage();
            if (mounted.current && request === latestRequest.current) {
                setReport(next);
                setLoadedAt(new Date());
                setError(null);
            }
        } catch (e) {
            if (mounted.current && request === latestRequest.current) {
                setError(e instanceof Error ? e.message : 'Failed to load LLM usage');
            }
        } finally {
            if (mounted.current) {
                setLoading(false);
            }
        }
    }, []);

    const refresh = useCallback(async (): Promise<void> => {
        setRefreshing(true);
        try {
            await load();
        } finally {
            if (mounted.current) {
                setRefreshing(false);
            }
        }
    }, [load]);

    useEffect(() => {
        mounted.current = true;
        if (!enabled) {
            return (): void => {
                mounted.current = false;
            };
        }
        void load();
        const timer = window.setInterval(() => {
            void load();
        }, POLL_INTERVAL_MS);
        return (): void => {
            mounted.current = false;
            window.clearInterval(timer);
        };
    }, [enabled, load]);

    return {report, error, loading, refreshing, loadedAt, refresh};
}
